import { Link, useNavigate } from 'react-router-dom'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { Form } from 'semantic-ui-react'
import {
    DangerText,
    LoaderBar,
    ScreenHeight,
    TitleText,
    ImageContainer,
    FlexRowBetween,
    AppDownloadButtons,
} from 'components'
import { AuthService, getPostLoginLandingPath, shouldHonorReturnUrl } from 'utility'
import { resolveHomeLanding } from 'utility/home-dashboard'
import { InspectionTaskService } from 'utility/services/inspection-task'
import { TemplateService } from 'utility/services/template'
import { useAuth } from 'context/user'
import {
    AppPrimaryButton,
    CommanAuthScreenLogin,
    FormTextFormField,
    FormPasswordFormField,
} from 'elements'
import { Images } from 'assets'
import { useTabletAndBelowMediaQuery } from 'layout'

const LoginSchema = yup.object().shape({
    username: yup
        .string()
        .trim('Username does not allowed spaces')
        .strict()
        .required('Username is required'),
    password: yup.string().required('Password is required'),
})

export const RestaurantExcellenceLoginScreen = () => {
    const isMobile = useTabletAndBelowMediaQuery()
    const navigate = useNavigate()
    const authContext = useAuth()
    const [processing, setProcessing] = useState('')
    const [error, setError] = useState('')

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(LoginSchema),
    })

    const userHasTemplates = async () => {
        try {
            const response = await TemplateService.templateList({ PageNumber: 1, PageSize: 1 })
            const total = response?.TotalCount
            const itemsLength = Array.isArray(response?.Items) ? response.Items.length : 0
            return (typeof total === 'number' ? total : itemsLength) > 0
        } catch (e) {
            return false
        }
    }

    const userHasTasks = async () => {
        try {
            const result = await InspectionTaskService.list({ PageNumber: 1, PageSize: 1, SearchText: '', Status: 0, UserId: '' })
            const total = result?.TotalCount
            const itemsLength = Array.isArray(result?.Items) ? result.Items.length : 0
            return (typeof total === 'number' ? total : itemsLength) > 0
        } catch (e) {
            return false
        }
    }

    const submit = async formData => {
        try {
            setError('')
            setProcessing('Processing')
            const result = await AuthService.login({
                LoginId: formData.username,
                Password: formData.password,
            })
            authContext.login(result)

            const [hasTasks, hasTemplates] = await Promise.all([
                userHasTasks(),
                userHasTemplates(),
            ])

            const params = new URLSearchParams(window.location.search)
            const returnUrl = params.get('returnUrl')

            if (returnUrl && shouldHonorReturnUrl(returnUrl, { hasTasks, hasTemplates })) {
                const decodedUrl = decodeURIComponent(returnUrl)
                try {
                    if (decodedUrl.startsWith('/app/task')) {
                        sessionStorage.setItem('shouldRedirectIfNoTasks', '1')
                    } else {
                        sessionStorage.removeItem('shouldRedirectIfNoTasks')
                    }
                } catch (err) { if (err) console.debug(err) }
                navigate(decodedUrl)
            } else {
                const dashboardId = result?.DashboardStyle?.DashboardId
                if (dashboardId === 2) {
                    navigate('/app/dashboard/groups')
                } else {
                    // /app/task becomes /app/home only for a company that switched the new home dashboard on
                    const target = await resolveHomeLanding(
                        result,
                        getPostLoginLandingPath(result, {
                            hasTasks,
                            hasTemplates,
                        }),
                    )
                    try {
                        if (target === '/app/task') {
                            sessionStorage.setItem('shouldRedirectIfNoTasks', '1')
                        } else {
                            sessionStorage.removeItem('shouldRedirectIfNoTasks')
                        }
                    } catch (err) { if (err) console.debug(err) }
                    navigate(target)
                }
            }
        } catch (error) {
            setError('Invalid username and/or password')
        } finally {
            setProcessing('')
        }
    }

    return (
        <ScreenHeight className="container">
            <CommanAuthScreenLogin isMobile={isMobile}>
                {processing && <LoaderBar content={processing} />}
                <TitleText>
                    <ImageContainer
                        src="/images/franchising-xpert.png"
                        alt="Franchising Xpert"
                        height={isMobile ? '40px' : '56px'}
                        width="auto"
                        style={{ display: 'block', margin: '0 auto 0.5rem auto', objectFit: 'contain' }}
                    />
                </TitleText>
                <Form noValidate onSubmit={handleSubmit(submit)} className="mt-5">
                    <FormTextFormField
                        control={control}
                        label="Username"
                        name="username"
                        placeholder="Username"
                        errors={errors?.username}
                        defaultValue=""
                    />
                    <FormPasswordFormField
                        control={control}
                        label="Password"
                        name="password"
                        placeholder="Password"
                        errors={errors?.password}
                        defaultValue=""
                        type="password"
                    />
                    <Link to="/forgot-password" className="d-flex justify-content-end">
                        <div basic className="">
                            Forgot Password?
                        </div>
                    </Link>
                    {error && <DangerText>{error}</DangerText>}
                    <FlexRowBetween>
                        <AppPrimaryButton fluid className="mt-2">
                            Login
                        </AppPrimaryButton>
                    </FlexRowBetween>
                    <Link to="/verification" className="d-flex justify-content-center">
                        <div basic className="mt-3">User verification by code</div>
                    </Link>
                    <div className="d-flex justify-content-center mt-3">
                        <ImageContainer src={Images.Logo} className="img" />
                    </div>
                </Form>
                <div className="mt-4">
                    <AppDownloadButtons />
                </div>
            </CommanAuthScreenLogin>
        </ScreenHeight>
    )
}

//
